import type { LineItem, LineItemSubItem, UnitOfMeasure } from '@/types/quotation';
import { generateId } from '@/lib/utils';
import {
  getUomCategory,
  calculateArea,
  calculateNetTotal,
  roundCurrency
} from './pricingEngine';

export function normalizeLocationUom(uom?: string): string {
  return (uom || '').trim().toLowerCase();
}

export function locationUsesArea(uom?: string): boolean {
  return getUomCategory(uom) === 'AREA';
}

export function locationUsesLinearMeasure(uom?: string): boolean {
  return getUomCategory(uom) === 'LINEAR';
}

export function locationNeedsDimensions(uom?: string): boolean {
  const cat = getUomCategory(uom);
  return cat === 'AREA' || cat === 'LINEAR' || cat === 'VOLUME';
}

export function calculateLocationArea(width?: number, height?: number): number {
  if (!width || !height) return 0;
  // Fallback to cm for legacy behavior where unit wasn't specified
  return calculateArea(width, height, 'cm');
}

export function calculateLocationSubTotal(
  subItem: LineItemSubItem,
  parentRate: number,
): number {
  const effectiveRate = subItem.rate !== undefined ? subItem.rate : parentRate;

  if (subItem.isLumpsum) {
    return roundCurrency(effectiveRate || 0);
  }

  const { total } = calculateNetTotal(
    Number(subItem.quantity || 0),
    effectiveRate,
    subItem.uom,
    subItem.dimensions
  );

  return roundCurrency(total);
}

export function calculateLocationRollup(subItems: LineItemSubItem[]): Pick<LineItem, 'quantity' | 'total'> {
  const total = roundCurrency(
    subItems.reduce((sum, si) => sum + Number(si.subTotal || 0), 0)
  );

  // Note: we still need to calculate the rolled up quantity depending on the UOM
  const quantity = roundCurrency(
    subItems.reduce((sum, si) => {
      const itemQty = Number(si.quantity || 0);
      const cat = getUomCategory(si.uom);
      
      const { multiplier } = calculateNetTotal(1, 1, si.uom, si.dimensions);
      
      if (cat === 'AREA' || cat === 'LINEAR' || cat === 'VOLUME') {
        return sum + (multiplier * itemQty);
      }
      return sum + itemQty;
    }, 0)
  );

  return { quantity, total };
}

export function createDefaultLocationSubItem(
  parentItem: LineItem,
  locationIndex: number,
  roomName?: string,
): LineItemSubItem {
  const defaultUom = (parentItem.uom || 'Sqft') as UnitOfMeasure;
  const dimsEnabled = locationNeedsDimensions(defaultUom);
  const inheritedWidth = parentItem.dimensions?.width || 0;
  const inheritedHeight = parentItem.dimensions?.height || 0;
  const unit = parentItem.dimensions?.unit || 'cm';
  
  const sqft = locationUsesArea(defaultUom)
    ? calculateArea(inheritedWidth, inheritedHeight, unit)
    : 0;

  return {
    id: generateId(),
    label: roomName ? `${roomName} Location ${locationIndex}` : `Location ${locationIndex}`,
    description: '',
    quantity: 1,
    uom: defaultUom,
    dimensions: dimsEnabled
      ? {
          width: inheritedWidth,
          height: locationUsesLinearMeasure(defaultUom) ? 0 : inheritedHeight,
          sqft,
          unit,
        }
      : undefined,
    subTotal: 0,
  };
}
